import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Utility
 */
const isNeg = (v) => typeof v === "string" && v.trim().startsWith("-");
const cellColor = (v) => (isNeg(v) ? "text-red-600" : "text-emerald-600");

/**
 * DATA MODEL
 */
const STRATEGIES = [
  {
    key: "aegis",
    label: "Aegis",
    subtitle: "High-conviction momentum execution on Bitcoin",
    profitability: {
      2018: {
        Jan: "7.38%", Feb: "8.70%", Mar: "23.49%", Apr: "-0.51%",
        May: "17.28%", Jun: "-9.93%", Jul: "14.09%", Aug: "-9.75%",
        Sep: "-9.22%", Oct: "-11.89%", Nov: "19.00%", Dec: "-0.44%",
        Total: "TBD",
      },
      2019: {
        Jan: "5.50%", Feb: "9.61%", Mar: "6.11%", Apr: "11.01%",
        May: "30.64%", Jun: "30.77%", Jul: "14.79%", Aug: "14.17%",
        Sep: "18.13%", Oct: "17.19%", Nov: "13.94%", Dec: "6.67%",
        Total: "TBD",
      },
      2020: {
        Jan: "22.49%", Feb: "0.52%", Mar: "9.93%", Apr: "18.65%",
        May: "-7.91%", Jun: "4.63%", Jul: "23.89%", Aug: "12.82%",
        Sep: "-2.69%", Oct: "13.75%", Nov: "21.55%", Dec: "3.79%",
        Total: "TBD",
      },
      2021: {
        Jan: "7.43%", Feb: "18.95%", Mar: "-0.36%", Apr: "-12.01%",
        May: "1.26%", Jun: "2.80%", Jul: "-2.43%", Aug: "-0.52%",
        Sep: "0.73%", Oct: "26.19%", Nov: "3.48%", Dec: "5.37%",
        Total: "TBD",
      },
      2022: {
        Jan: "12.93%", Feb: "9.49%", Mar: "0.46%", Apr: "6.60%",
        May: "-2.71%", Jun: "0.80%", Jul: "3.83%", Aug: "-12.88%",
        Sep: "6.80%", Oct: "13.68%", Nov: "-2.79%", Dec: "9.29%",
        Total: "TBD",
      },
      2023: {
        Jan: "34.27%", Feb: "-11.19%", Mar: "20.25%", Apr: "-4.14%",
        May: "7.49%", Jun: "23.10%", Jul: "-6.15%", Aug: "-4.02%",
        Sep: "-8.38%", Oct: "20.84%", Nov: "-0.88%", Dec: "7.81%",
        Total: "TBD",
      },
      2024: {
        Jan: "3.55%", Feb: "33.30%", Mar: "-10.63%", Apr: "3.35%",
        May: "-6.82%", Jun: "16.79%", Jul: "10.94%", Aug: "7.67%",
        Sep: "16.63%", Oct: "17.70%", Nov: "10.84%", Dec: "-10.67%",
        Total: "TBD",
      },
      2025: {
        Jan: "10.71%", Feb: "10.32%", Mar: "10.65%", Apr: "13.29%",
        May: "13.02%", Jun: "6.06%", Jul: "3.88%", Aug: "-1.05%",
        Sep: "-6.69%", Oct: "38.30%", Nov: "23.75%", Dec: "-23.76%",
        Total: "TBD",
      },
      2026: {
        Jan: "14.98%", Feb: "-", Mar: "-", Apr: "-",
        May: "-", Jun: "-", Jul: "-", Aug: "-",
        Sep: "-", Oct: "-", Nov: "-", Dec: "-",
        Total: "TBD",
      },
    },
    performance: [
      { label: "Net Profit", value: "729.65%" },
      { label: "Win Rate", value: "38.50%" },
      { label: "Profit Factor", value: "1.49" },
      { label: "Incubation Profit", value: "183.35%" },
    ],
    risk: [
      { label: "Maximum Drawdown", value: "25.77%" },
    ],
  },

  {
    key: "athena",
    label: "Athena",
    subtitle: "Multi-layer trend capture on Solana",
    profitability: {
      2021: {
        Jan: "-", Feb: "-", Mar: "-", Apr: "-",
        May: "-", Jun: "-", Jul: "-", Aug: "6.57%",
        Sep: "7.48%", Oct: "2.09%", Nov: "11.37%", Dec: "1.74%",
        Total: "TBD",
      },
      2022: {
        Jan: "1.55%", Feb: "17.97%", Mar: "37.46%", Apr: "7.81%",
        May: "27.15%", Jun: "23.23%", Jul: "-17.48%", Aug: "17.80%",
        Sep: "4.80%", Oct: "24.02%", Nov: "-12.29%", Dec: "7.83%",
        Total: "TBD",
      },
      2023: {
        Jan: "27.10%", Feb: "11.56%", Mar: "30.20%", Apr: "4.70%",
        May: "-5.21%", Jun: "17.97%", Jul: "20.16%", Aug: "14.21%",
        Sep: "14.03%", Oct: "36.97%", Nov: "13.58%", Dec: "1.81%",
        Total: "TBD",
      },
      2024: {
        Jan: "1.67%", Feb: "4.55%", Mar: "0.92%", Apr: "23.48%",
        May: "10.84%", Jun: "30.42%", Jul: "24.56%", Aug: "14.20%",
        Sep: "4.82%", Oct: "26.73%", Nov: "-4.85%", Dec: "4.31%",
        Total: "TBD",
      },
      2025: {
        Jan: "46.50%", Feb: "24.27%", Mar: "14.24%", Apr: "-5.01%",
        May: "5.25%", Jun: "-1.35%", Jul: "11.26%", Aug: "11.88%",
        Sep: "26.75%", Oct: "5.25%", Nov: "-0.96%", Dec: "-33.87%",
        Total: "TBD",
      },
      2026: {
        Jan: "3.96%", Feb: "-", Mar: "-", Apr: "-",
        May: "-", Jun: "-", Jul: "-", Aug: "-",
        Sep: "-", Oct: "-", Nov: "-", Dec: "-",
        Total: "TBD",
      },
    },
    performance: [
      { label: "Net Profit", value: "606.00%" },
      { label: "Win Rate", value: "42.04%" },
      { label: "Profit Factor", value: "1.54" },
      { label: "Incubation Profit", value: "134.36%" },
    ],
    risk: [
      { label: "Maximum Drawdown", value: "14.80%" },
    ],
  },

  {
    key: "orion",
    label: "Orion",
    subtitle: "Long-term momentum framework on Ethereum",
    profitability: {
      2020: {
        Jan: "-", Feb: "-", Mar: "-", Apr: "-",
        May: "-", Jun: "-", Jul: "-", Aug: "-",
        Sep: "-", Oct: "-", Nov: "1.34%", Dec: "5.29%",
        Total: "TBD",
      },
      2021: {
        Jan: "11.87%", Feb: "13.24%", Mar: "-6.46%", Apr: "-6.49%",
        May: "13.22%", Jun: "16.27%", Jul: "18.93%", Aug: "-3.79%",
        Sep: "7.10%", Oct: "-2.14%", Nov: "2.22%", Dec: "4.07%",
        Total: "TBD",
      },
      2022: {
        Jan: "17.47%", Feb: "29.46%", Mar: "12.57%", Apr: "-6.65%",
        May: "8.38%", Jun: "16.24%", Jul: "-1.98%", Aug: "14.49%",
        Sep: "-15.63%", Oct: "0.69%", Nov: "17.55%", Dec: "-8.09%",
        Total: "TBD",
      },
      2023: {
        Jan: "22.83%", Feb: "-4.86%", Mar: "8.29%", Apr: "7.99%",
        May: "-11.20%", Jun: "4.70%", Jul: "-21.76%", Aug: "23.69%",
        Sep: "-", Oct: "21.25%", Nov: "-3.75%", Dec: "-22.20%",
        Total: "TBD",
      },
      2024: {
        Jan: "8.18%", Feb: "31.99%", Mar: "-5.19%", Apr: "8.22%",
        May: "0.81%", Jun: "-2.41%", Jul: "27.17%", Aug: "3.33%",
        Sep: "5.06%", Oct: "14.22%", Nov: "24.92%", Dec: "2.64%",
        Total: "TBD",
      },
      2025: {
        Jan: "-23.09%", Feb: "20.57%", Mar: "-2.33%", Apr: "6.94%",
        May: "6.65%", Jun: "-1.12%", Jul: "19.92%", Aug: "6.95%",
        Sep: "9.25%", Oct: "10.08%", Nov: "0.99%", Dec: "-5.27%",
        Total: "TBD",
      },
      2026: {
        Jan: "8.86%", Feb: "-", Mar: "-", Apr: "-",
        May: "-", Jun: "-", Jul: "-", Aug: "-",
        Sep: "-", Oct: "-", Nov: "-", Dec: "-",
        Total: "TBD",
      },
    },
    performance: [
      { label: "Net Profit", value: "361.47%" },
      { label: "Win Rate", value: "35.03%" },
      { label: "Profit Factor", value: "1.30" },
      { label: "Incubation Profit", value: "100.19%" },
    ],
    risk: [
      { label: "Maximum Drawdown", value: "14.52%" },
    ],
  },
];

/**
 * TABLE HEADERS (months)
 */
const MONTH_ORDER = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function ProofOfPerformance() {
  const [active, setActive] = useState(0);
  const [expandedYear, setExpandedYear] = useState(null);
  const s = STRATEGIES[active];

  return (
    <section id="performance" className="py-24 px-6 bg-neutral-50">
      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Performance verified on-chain.
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
            Partner with QuantNexus Capital to integrate your strategies, custody solutions, or institutional frameworks into our automated vault infrastructure.
            Together, we create the connective layer between traditional finance and decentralized markets.
          </p>

          {/* NEW NOTE */}
          <motion.p
            className="text-sm text-gray-500 max-w-2xl mx-auto italic"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            These results represent performance data for{" "}
            <span className="font-semibold text-cyan-700">3 of our Elite</span> 
            {" "}active strategies currently available for allocation. 
            Contact us to request details on the remaining strategies and 
            institutional portfolios.
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-white border border-gray-200 p-1.5 rounded-full shadow-sm">
            {STRATEGIES.map((st, i) => (
              <button
                key={st.key}
                onClick={() => {
                  setActive(i);
                  setExpandedYear(null);
                }}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  active === i
                    ? "bg-cyan-600 text-white shadow-md"
                    : "text-gray-600 hover:text-cyan-700"
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-center text-sm text-gray-500 mb-8">{s.subtitle}</p>

        {/* Profitability Section */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 md:p-8 mb-12">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Profitability</h3>

          {Object.entries(s.profitability).map(([year, months]) => {
            const hasMonths = MONTH_ORDER.some((m) => months[m] && months[m] !== "-");
            const isOpen = expandedYear === year;

            return (
              <div key={year} className="mb-4">
                <button
                  onClick={() => setExpandedYear(isOpen ? null : year)}
                  className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-4 rounded-lg transition"
                >
                  <span className="font-medium text-gray-800">{year}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-xs">
                      {hasMonths ? (isOpen ? "Hide months" : "Show months") : "Yearly total"}
                    </span>
                    <span className="font-bold text-cyan-700">{months.Total}</span>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && hasMonths && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35 }}
                      className="overflow-hidden"
                    >
                      <div className="overflow-x-auto">
                        <table className="w-full mt-4 text-sm">
                          <thead>
                            <tr className="text-gray-500">
                              {MONTH_ORDER.filter((m) => months[m] && months[m] !== "-").map((m) => (
                                <th key={m} className="px-2 py-1 text-left">
                                  {m}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              {MONTH_ORDER.filter((m) => months[m] && months[m] !== "-").map((m) => (
                                <td
                                  key={m}
                                  className={`px-2 py-1 ${cellColor(months[m])}`}
                                >
                                  {months[m]}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Indicators */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-gray-200 rounded-xl shadow-md p-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Indicators</h4>
            <ul className="space-y-2">
              {s.performance.map((p, i) => (
                <li key={i} className="flex justify-between text-gray-700">
                  <span>{p.label}</span>
                  <span className="font-semibold text-cyan-700">{p.value}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-md p-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk Indicators</h4>
            <ul className="space-y-2">
              {s.risk.map((r, i) => (
                <li key={i} className="flex justify-between text-gray-700">
                  <span>{r.label}</span>
                  <span className="font-semibold text-red-600">{r.value}</span>
                </li>
              ))}
            </ul> 
          </div>
        </div>

        {/* Disclaimer */}
        <p className="mt-10 text-center text-xs text-gray-400 italic">
          Past performance is not indicative of future results. Strategy figures reflect
          live-tracked performance with on-chain fee enforcement above the high-watermark.
        </p>
      </div>
    </section>
  );
}